/**
 * Conditional Access Policies Management Component
 *
 * Displays and manages Conditional Access policies that enforce passwordless authentication.
 * Supports policy listing, creation, enabling/disabling, and deletion.
 *
 * Phase 3 - TAP Passwordless Authentication
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Button,
  Spinner,
  makeStyles,
  tokens,
  Body1,
  Body1Strong,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  TableColumnDefinition,
  TableCellLayout,
  createTableColumn,
} from '@fluentui/react-components';
import {
  DeleteRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { DataGridCard } from '../../layouts';
import { createColumnWithRowActions, type ActionConfig } from '../../layouts/factories/columns';
import { useConditionalAccessPolicies } from '../../../hooks/useConditionalAccessPolicies';
import type { ConditionalAccessPolicyResponse } from '../../../types/conditionalAccess';
import { Alert } from '../../common/Alert';
import { PolicyConfigModal } from './PolicyConfigModal';
import {
  shouldShowEditMenuItem,
  transformPolicyForEdit,
} from './policyTypeUtils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
});

interface ConditionalAccessPoliciesProps {
  tenantId: string;
  onSelectionChange: (selection: Set<string>) => void;
  onPolicyCountChange: (count: number) => void;
}

export interface ConditionalAccessPoliciesHandle {
  openBulkDeleteDialog: () => void;
}

export const ConditionalAccessPolicies = forwardRef<
  ConditionalAccessPoliciesHandle,
  ConditionalAccessPoliciesProps
>(({
  tenantId,
  onSelectionChange,
  onPolicyCountChange,
}, ref) => {
  const styles = useStyles();

  // Use React Query hook for data fetching and mutations
  const {
    policies: rawPolicies,
    isLoading,
    error: fetchError,
    refetch,
    deletePolicy,
    bulkDeletePolicies,
    isBulkDeleting,
  } = useConditionalAccessPolicies(tenantId);

  // Ensure policies is always an array
  const policies = rawPolicies ?? [];

  // Local UI state
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<ConditionalAccessPolicyResponse | null>(null);

  // Selection state for multi-select checkboxes
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(new Set());

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    openBulkDeleteDialog: () => {
      if (selectedPolicies.size > 0) {
        setBulkDeleteDialogOpen(true);
      }
    },
  }));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Policy Config Modal state
  const [policyConfigModalOpen, setPolicyConfigModalOpen] = useState(false);
  const [policyConfigMode, setPolicyConfigMode] = useState<'create' | 'edit'>('create');
  const [policyConfigInitialPolicy, setPolicyConfigInitialPolicy] = useState<{
    id: string;
    displayName: string;
    policy_type: string;
    sign_in_frequency_hours?: number;
    sign_in_frequency_days?: number;
    persistent_browser_mode?: 'always' | 'never';
    cae_mode?: 'strictEnforcement' | 'disabled' | 'strictLocation';
    state?: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced';
    exclude_users?: string[];
  } | undefined>(undefined);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Reset to page 1 when policies list changes (after delete/create)
  useEffect(() => {
    setCurrentPage(1);
  }, [policies.length]);

  // Notify parent of policy count changes
  useEffect(() => {
    onPolicyCountChange(policies.length);
  }, [policies.length, onPolicyCountChange]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange(selectedPolicies);
  }, [selectedPolicies, onSelectionChange]);

  // transformPolicyForEdit is now imported from policyTypeUtils

  const openDeleteDialog = (policy: ConditionalAccessPolicyResponse) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeletePolicy = async () => {
    if (!policyToDelete) return;

    try {
      setActionLoading(policyToDelete.id);
      await deletePolicy(policyToDelete.id);
      setSuccess(`Policy "${policyToDelete.displayName}" deleted successfully`);
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
    } catch {
      // Error is captured in hook's deleteError
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedPolicies);

    try {
      await bulkDeletePolicies(selectedIds);
      setSelectedPolicies(new Set());
      setBulkDeleteDialogOpen(false);
    } catch {
      // Error is captured in hook's bulkDeleteError
      setBulkDeleteDialogOpen(false);
    }
  };

  const getStateBadge = (state: string) => {
    switch (state.toLowerCase()) {
      case 'enabled':
        return <Badge color="success" appearance="filled">Enabled</Badge>;
      case 'disabled':
        return <Badge color="danger" appearance="filled">Disabled</Badge>;
      case 'enabledforreportingbutnotenforced':
        return <Badge color="warning" appearance="filled">Report-only</Badge>;
      default:
        return <Badge appearance="outline">{state}</Badge>;
    }
  };

  // Column sizing options
  const columnSizingOptions = {
    displayName: { minWidth: 150, defaultWidth: 300 },
    state: { minWidth: 80, defaultWidth: 120 },
    createdDateTime: { minWidth: 120, defaultWidth: 180 },
    modifiedDateTime: { minWidth: 120, defaultWidth: 180 },
  };

  const columns: TableColumnDefinition<ConditionalAccessPolicyResponse>[] = [
    createColumnWithRowActions<ConditionalAccessPolicyResponse>({
      columnId: 'displayName',
      header: 'Policy Name',
      compare: (a, b) => a.displayName.localeCompare(b.displayName),
      renderContent: (item) => (
        <span title={item.displayName} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.displayName}
        </span>
      ),
      getActions: (item) => {
        const actions: ActionConfig[] = [];
        if (shouldShowEditMenuItem(item)) {
          actions.push({
            label: 'Edit',
            icon: <EditRegular />,
            onClick: () => {
              setPolicyConfigMode('edit');
              setPolicyConfigInitialPolicy(transformPolicyForEdit(item));
              setPolicyConfigModalOpen(true);
            },
          });
        }
        actions.push({
          label: 'Delete Policy',
          icon: <DeleteRegular />,
          onClick: () => openDeleteDialog(item),
        });
        return actions;
      },
      getAriaLabel: (item) => `Actions for ${item.displayName}`,
    }),
    createTableColumn<ConditionalAccessPolicyResponse>({
      columnId: 'state',
      compare: (a, b) => a.state.localeCompare(b.state),
      renderHeaderCell: () => 'State',
      renderCell: (item) => (
        <TableCellLayout>
          {getStateBadge(item.state)}
        </TableCellLayout>
      ),
    }),
    createTableColumn<ConditionalAccessPolicyResponse>({
      columnId: 'createdDateTime',
      compare: (a, b) => {
        const dateA = a.createdDateTime ? new Date(a.createdDateTime).getTime() : 0;
        const dateB = b.createdDateTime ? new Date(b.createdDateTime).getTime() : 0;
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Created',
      renderCell: (item) => (
        <TableCellLayout>
          {item.createdDateTime
            ? new Date(item.createdDateTime).toLocaleString()
            : 'N/A'}
        </TableCellLayout>
      ),
    }),
    createTableColumn<ConditionalAccessPolicyResponse>({
      columnId: 'modifiedDateTime',
      compare: (a, b) => {
        const dateA = a.modifiedDateTime ? new Date(a.modifiedDateTime).getTime() : 0;
        const dateB = b.modifiedDateTime ? new Date(b.modifiedDateTime).getTime() : 0;
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Modified',
      renderCell: (item) => (
        <TableCellLayout>
          {item.modifiedDateTime
            ? new Date(item.modifiedDateTime).toLocaleString()
            : 'N/A'}
        </TableCellLayout>
      ),
    }),
  ];

  // Calculate pagination
  const totalPages = Math.ceil(policies.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPolicies = policies.slice(startIndex, endIndex);

  // Handle selection change from DataGridCard
  const handleSelectionChangeFromGrid = (ids: Set<string | number>) => {
    const stringIds = new Set<string>();
    ids.forEach((id) => stringIds.add(String(id)));
    setSelectedPolicies(stringIds);
  };

  return (
    <div className={styles.container}>
      {fetchError && <Alert intent="error">{fetchError.message}</Alert>}
      {success && <Alert intent="success">{success}</Alert>}

      <DataGridCard
        items={paginatedPolicies}
        columns={columns}
        getRowId={(item) => item.id}
        columnSizingOptions={columnSizingOptions}
        selectable
        selectedIds={selectedPolicies}
        onSelectionChange={handleSelectionChangeFromGrid}
        pagination={{
          showing: paginatedPolicies.length,
          total: policies.length,
          page: currentPage,
          totalPages: totalPages || 1,
          onPageChange: setCurrentPage,
        }}
        loading={isLoading}
        emptyMessage="No Conditional Access policies found"
        testId="conditional-access-policies-table"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Conditional Access Policy</DialogTitle>
            <DialogContent>
              {actionLoading === policyToDelete?.id ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.spacingVerticalL,
                  padding: tokens.spacingVerticalXXL
                }}>
                  <Spinner size="large" label="Deleting policy..." />
                  <Body1>Please wait while the policy is being deleted.</Body1>
                </div>
              ) : (
                <>
                  Are you sure you want to delete the policy &ldquo;{policyToDelete?.displayName}&rdquo;? This action cannot be undone.
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={actionLoading === policyToDelete?.id}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeletePolicy}
                disabled={actionLoading !== null}
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(_, data) => setBulkDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogContent>
              {isBulkDeleting ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.spacingVerticalL,
                  padding: tokens.spacingVerticalXXL
                }}>
                  <Spinner size="large" label="Deleting policies..." />
                  <Body1>Please wait while policies are being deleted.</Body1>
                </div>
              ) : (
                <>
                  <Body1>
                    Are you sure you want to delete {selectedPolicies.size} {selectedPolicies.size === 1 ? 'policy' : 'policies'}? This action cannot be undone.
                  </Body1>
                  <div style={{ marginTop: '16px' }}>
                    <Body1Strong>Policies to be deleted:</Body1Strong>
                    <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                      {Array.from(selectedPolicies).map(policyId => {
                        const policy = policies.find(p => p.id === policyId);
                        if (policy) {
                          return (
                            <li key={policyId} style={{ marginBottom: '4px' }}>
                              {policy.displayName}
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setBulkDeleteDialogOpen(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Policy Config Modal */}
      <PolicyConfigModal
        isOpen={policyConfigModalOpen}
        mode={policyConfigMode}
        initialPolicy={policyConfigInitialPolicy}
        onClose={() => setPolicyConfigModalOpen(false)}
        tenantId={tenantId}
        onSuccess={() => {
          setPolicyConfigModalOpen(false);
          refetch();
        }}
      />
    </div>
  );
});

ConditionalAccessPolicies.displayName = "ConditionalAccessPolicies";
